import React from "react"
import { SectionCard, Field, MetricPill } from "./ClinicalHistoryAtoms"
import {
  inputStyle,
  textareaStyle,
  buttonStyle,
  primaryButtonStyle,
  modalOverlayStyle,
  modalCardStyle,
} from "./ClinicalHistoryStyles"

function translateStatusLabel(value) {
  switch (String(value || "").toUpperCase()) {
    case "DRAFT":
      return "Borrador"
    case "IN_PROGRESS":
      return "En curso"
    case "FOLLOW_UP":
      return "En seguimiento"
    case "COMPLETED":
      return "Completado"
    case "CANCELLED":
      return "Cancelado"
    case "REGISTERED":
      return "Registrada"
    case "CLOSED":
      return "Cerrada"
    default:
      return value || "Nuevo"
  }
}

function ModalHeader({
  badgeText,
  badgeBg,
  badgeBorder,
  badgeColor,
  title,
  description,
  latestInstance,
  onClose,
}) {
  return (
    <div
      style={{
        padding: 16,
        borderBottom: "1px solid #e2e8f0",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: 12,
        alignItems: "start",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "inline-flex",
            padding: "6px 10px",
            borderRadius: 999,
            background: badgeBg,
            border: `1px solid ${badgeBorder}`,
            color: badgeColor,
            fontSize: 11,
            fontWeight: 900,
            marginBottom: 8,
            letterSpacing: 0.4,
            textTransform: "uppercase",
          }}
        >
          {badgeText}
        </div>

        <div
          style={{
            fontSize: 22,
            fontWeight: 900,
            color: "#0f172a",
            lineHeight: 1.04,
            letterSpacing: "-0.03em",
          }}
        >
          {title}
        </div>

        <div
          style={{
            fontSize: 13,
            color: "#64748b",
            marginTop: 6,
            lineHeight: 1.5,
            maxWidth: 760,
          }}
        >
          {description}
        </div>

        {latestInstance !== undefined ? (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 10,
            }}
          >
            <MetricPill
              label="Último estado"
              value={translateStatusLabel(latestInstance?.status)}
              tone={latestInstance ? "info" : "neutral"}
            />

            <MetricPill
              label="Última edición"
              value={
                latestInstance?.updatedAt
                  ? new Date(latestInstance.updatedAt).toLocaleDateString("es-MX")
                  : "Sin registro"
              }
              tone={latestInstance ? "success" : "neutral"}
            />
          </div>
        ) : null}
      </div>

      <button onClick={onClose} style={buttonStyle}>
        Cerrar
      </button>
    </div>
  )
}

function ReadOnlyBanner({ isReadOnly, readOnlyReason }) {
  if (!isReadOnly) return null

  return (
    <div
      style={{
        margin: "14px 18px 0",
        border: "1px solid #fecaca",
        background: "#fef2f2",
        color: "#991b1b",
        borderRadius: 12,
        padding: 12,
        fontWeight: 700,
      }}
    >
      🔒 Modo solo lectura — {readOnlyReason}
    </div>
  )
}

function ModalFooter({
  onClose,
  onSecondary,
  secondaryLabel = "",
  onPrimary,
  primaryLabel,
  disabled = false,
  loadingLabel = "Procesando...",
}) {
  return (
    <div
      style={{
        padding: 16,
        borderTop: "1px solid #e2e8f0",
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <button onClick={onClose} style={buttonStyle}>
        Cerrar
      </button>

      {secondaryLabel ? (
        <button onClick={onSecondary} disabled={disabled} style={buttonStyle}>
          {disabled ? loadingLabel : secondaryLabel}
        </button>
      ) : null}

      <button
        onClick={onPrimary}
        disabled={disabled}
        style={{
          ...primaryButtonStyle,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.8 : 1,
        }}
      >
        {disabled ? loadingLabel : primaryLabel}
      </button>
    </div>
  )
}

function ReadOnlyValue({ value, multiline = false }) {
  if (multiline) {
    return (
      <textarea
        readOnly
        value={value || ""}
        style={{ ...textareaStyle, background: "#f8fafc" }}
      />
    )
  }

  return (
    <input
      readOnly
      value={value || ""}
      style={{ ...inputStyle, background: "#f8fafc" }}
    />
  )
}

export function DiagnosisInitialModal({
  open,
  onClose,
  form,
  setForm,
  saving,
  onSave,
  latestInstance,
  isReadOnly,
  readOnlyReason,
}) {
  if (!open) return null

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={{ ...modalCardStyle, width: "min(1080px, 100%)" }}>
        <ModalHeader
          badgeText="Diagnóstico clínico"
          badgeBg="#eff6ff"
          badgeBorder="#bfdbfe"
          badgeColor="#1d4ed8"
          title="Diagnóstico inicial"
          description="Captura el diagnóstico preliminar o confirmado y la conducta sugerida para integrarlo con odontograma y tratamiento."
          latestInstance={latestInstance}
          onClose={onClose}
        />

        <ReadOnlyBanner
          isReadOnly={isReadOnly}
          readOnlyReason={readOnlyReason}
        />

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <SectionCard
            title="1. Identificación diagnóstica"
            subtitle="Tipo, fecha y severidad"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Fecha">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Tipo">
                <select
                  value={form.diagnosisType}
                  onChange={(e) => updateField("diagnosisType", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="PRELIMINARY">Preliminar</option>
                  <option value="CONFIRMED">Confirmado</option>
                </select>
              </Field>

              <Field label="Severidad">
                <select
                  value={form.severity}
                  onChange={(e) => updateField("severity", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="LOW">Baja</option>
                  <option value="MEDIUM">Media</option>
                  <option value="HIGH">Alta</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="2. Diagnóstico"
            subtitle="Descripción principal y complementaria"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1.35fr) minmax(0,1fr)",
                gap: 12,
              }}
            >
              <Field label="Diagnóstico principal">
                <input
                  value={form.mainDiagnosis}
                  onChange={(e) => updateField("mainDiagnosis", e.target.value)}
                  placeholder="Ej. caries profunda, pulpitis irreversible, fractura coronaria"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Diagnóstico secundario">
                <input
                  value={form.secondaryDiagnosis}
                  onChange={(e) => updateField("secondaryDiagnosis", e.target.value)}
                  placeholder="Ej. inflamación pulpar, restauración filtrada"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="3. Ubicación clínica"
            subtitle="Zona afectada o referencia dental"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Área afectada">
                <input
                  value={form.affectedArea}
                  onChange={(e) => updateField("affectedArea", e.target.value)}
                  placeholder="Ej. sector superior derecho"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Pieza">
                <input
                  value={form.tooth}
                  onChange={(e) => updateField("tooth", e.target.value)}
                  placeholder="Ej. 16"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Superficie">
                <input
                  value={form.surface}
                  onChange={(e) => updateField("surface", e.target.value)}
                  placeholder="Ej. oclusal / distal"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Prioridad">
                <select
                  value={form.priority}
                  onChange={(e) => updateField("priority", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="LOW">Baja</option>
                  <option value="MEDIUM">Media</option>
                  <option value="HIGH">Alta</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="4. Conducta sugerida"
            subtitle="Qué sigue clínicamente"
            defaultOpen={true}
          >
            <Field label="Tratamiento recomendado">
              <textarea
                value={form.recommendedTreatment}
                onChange={(e) => updateField("recommendedTreatment", e.target.value)}
                placeholder="Ej. valoración endodóntica, resina, extracción, control clínico"
                style={textareaStyle}
                disabled={isReadOnly}
              />
            </Field>

            <div style={{ marginTop: 12 }}>
              <Field label="Notas clínicas">
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="5. Integración con el flujo"
            subtitle="Cómo se conecta con el resto del sistema"
            defaultOpen={false}
          >
            <div style={{ display: "grid", gap: 10 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 14,
                  color: "#334155",
                  fontWeight: 600,
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                  opacity: isReadOnly ? 0.75 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={form.linkedToOdontogram}
                  disabled={isReadOnly}
                  onChange={(e) => updateField("linkedToOdontogram", e.target.checked)}
                />
                Este diagnóstico debe considerarse ligado al odontograma.
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 14,
                  color: "#334155",
                  fontWeight: 600,
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                  opacity: isReadOnly ? 0.75 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={form.sendToTreatmentPlan}
                  disabled={isReadOnly}
                  onChange={(e) => updateField("sendToTreatmentPlan", e.target.checked)}
                />
                Este diagnóstico debe enviarse después al plan de tratamiento.
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 14,
                  color: "#334155",
                  fontWeight: 600,
                  cursor: isReadOnly ? "not-allowed" : "pointer",
                  opacity: isReadOnly ? 0.75 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={form.validatedClinically}
                  disabled={isReadOnly}
                  onChange={(e) => updateField("validatedClinically", e.target.checked)}
                />
                Confirmo que este diagnóstico fue valorado clínicamente.
              </label>
            </div>
          </SectionCard>
        </div>

        <ModalFooter
          onClose={onClose}
          onPrimary={onSave}
          primaryLabel="Guardar diagnóstico inicial"
          disabled={saving || isReadOnly}
          loadingLabel="Guardando..."
        />
      </div>
    </div>
  )
}

export function EndoHistoryModal({
  open,
  onClose,
  form,
  setForm,
  saving,
  onSave,
  latestInstance,
  isReadOnly,
  readOnlyReason,
}) {
  if (!open) return null

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={modalCardStyle}>
        <ModalHeader
          badgeText="Historia especializada"
          badgeBg="#fff7ed"
          badgeBorder="#fed7aa"
          badgeColor="#c2410c"
          title="Historia de Endodoncia"
          description="Captura motivo endodóntico, pruebas clínicas, diagnóstico pulpar/periapical y plan inicial."
          latestInstance={latestInstance}
          onClose={onClose}
        />

        <ReadOnlyBanner
          isReadOnly={isReadOnly}
          readOnlyReason={readOnlyReason}
        />

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <SectionCard
            title="1. Consulta endodóntica"
            subtitle="Motivo y pieza en estudio"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Fecha">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Pieza dental">
                <input
                  value={form.tooth}
                  onChange={(e) => updateField("tooth", e.target.value)}
                  placeholder="Ej. 16, 21, 36"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Motivo endodóntico de consulta">
                  <input
                    value={form.chiefComplaint}
                    onChange={(e) => updateField("chiefComplaint", e.target.value)}
                    placeholder="Ej. dolor intenso, sensibilidad persistente, revaloración"
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="2. Sintomatología"
            subtitle="Dolor y características relevantes"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="¿Dolor espontáneo?">
                <select
                  value={form.spontaneousPain}
                  onChange={(e) => updateField("spontaneousPain", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>

              <Field label="Tipo de dolor">
                <select
                  value={form.painType}
                  onChange={(e) => updateField("painType", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="AGUDO">Agudo</option>
                  <option value="PULSATIL">Pulsátil</option>
                  <option value="PROVOCADO">Provocado</option>
                  <option value="CONTINUO">Continuo</option>
                  <option value="INTERMITENTE">Intermitente</option>
                </select>
              </Field>

              <Field label="Nivel de urgencia">
                <select
                  value={form.urgencyLevel}
                  onChange={(e) => updateField("urgencyLevel", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="BAJA">Baja</option>
                  <option value="MEDIA">Media</option>
                  <option value="ALTA">Alta</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="3. Pruebas clínicas"
            subtitle="Respuesta a estímulos y exploración"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Prueba al frío">
                <select
                  value={form.coldTest}
                  onChange={(e) => updateField("coldTest", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="POSITIVA_NORMAL">Positiva normal</option>
                  <option value="POSITIVA_EXAGERADA">Positiva exagerada</option>
                  <option value="NEGATIVA">Negativa</option>
                </select>
              </Field>

              <Field label="Prueba al calor">
                <select
                  value={form.heatTest}
                  onChange={(e) => updateField("heatTest", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="POSITIVA">Positiva</option>
                  <option value="NEGATIVA">Negativa</option>
                </select>
              </Field>

              <Field label="Percusión">
                <select
                  value={form.percussion}
                  onChange={(e) => updateField("percussion", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="POSITIVA">Positiva</option>
                  <option value="NEGATIVA">Negativa</option>
                </select>
              </Field>

              <Field label="Palpación">
                <select
                  value={form.palpation}
                  onChange={(e) => updateField("palpation", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="POSITIVA">Positiva</option>
                  <option value="NEGATIVA">Negativa</option>
                </select>
              </Field>

              <Field label="Movilidad">
                <select
                  value={form.mobility}
                  onChange={(e) => updateField("mobility", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="0">0</option>
                  <option value="I">I</option>
                  <option value="II">II</option>
                  <option value="III">III</option>
                </select>
              </Field>

              <Field label="Sondaje">
                <input
                  value={form.probing}
                  onChange={(e) => updateField("probing", e.target.value)}
                  placeholder="Ej. normal / 6 mm distal"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Fístula">
                <select
                  value={form.fistula}
                  onChange={(e) => updateField("fistula", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>

              <Field label="Aumento de volumen">
                <select
                  value={form.swelling}
                  onChange={(e) => updateField("swelling", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="4. Diagnóstico"
            subtitle="Diagnóstico pulpar y periapical"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Diagnóstico pulpar">
                <select
                  value={form.pulpDiagnosis}
                  onChange={(e) => updateField("pulpDiagnosis", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="PULPA_SANA">Pulpa sana</option>
                  <option value="PULPITIS_REVERSIBLE">Pulpitis reversible</option>
                  <option value="PULPITIS_IRREVERSIBLE">Pulpitis irreversible</option>
                  <option value="NECROSIS_PULPAR">Necrosis pulpar</option>
                  <option value="TRATAMIENTO_PREVIO">Tratamiento previo</option>
                </select>
              </Field>

              <Field label="Diagnóstico periapical">
                <select
                  value={form.apicalDiagnosis}
                  onChange={(e) => updateField("apicalDiagnosis", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="NORMAL">Tejidos apicales normales</option>
                  <option value="PERIODONTITIS_SINTOMATICA">Periodontitis apical sintomática</option>
                  <option value="PERIODONTITIS_ASINTOMATICA">Periodontitis apical asintomática</option>
                  <option value="ABSCESO_APICAL_AGUDO">Absceso apical agudo</option>
                  <option value="ABSCESO_APICAL_CRONICO">Absceso apical crónico</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="5. Radiografía y hallazgos"
            subtitle="Observaciones auxiliares"
            defaultOpen={false}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Canales observados">
                <input
                  value={form.canalsObserved}
                  onChange={(e) => updateField("canalsObserved", e.target.value)}
                  placeholder="Ej. 3 conductos"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Estado restaurador">
                <input
                  value={form.restorationStatus}
                  onChange={(e) => updateField("restorationStatus", e.target.value)}
                  placeholder="Ej. resina amplia / corona / sin restauración"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="¿Sospecha de fisura/fractura?">
                <select
                  value={form.crackSuspicion}
                  onChange={(e) => updateField("crackSuspicion", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Hallazgos radiográficos">
                <textarea
                  value={form.radiographicFindings}
                  onChange={(e) => updateField("radiographicFindings", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="6. Plan inicial"
            subtitle="Conducta clínica recomendada"
            defaultOpen={false}
          >
            <Field label="Plan de tratamiento endodóntico">
              <textarea
                value={form.treatmentPlan}
                onChange={(e) => updateField("treatmentPlan", e.target.value)}
                placeholder="Ej. apertura cameral, medicación intracanal, endodoncia en 2 citas, retiro de restauración, etc."
                style={textareaStyle}
                disabled={isReadOnly}
              />
            </Field>

            <div style={{ marginTop: 12 }}>
              <Field label="Notas adicionales">
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="7. Validación"
            subtitle="Registro del profesional"
            defaultOpen={true}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                color: "#334155",
                fontWeight: 600,
                cursor: isReadOnly ? "not-allowed" : "pointer",
                opacity: isReadOnly ? 0.75 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={form.consentAccepted}
                disabled={isReadOnly}
                onChange={(e) => updateField("consentAccepted", e.target.checked)}
              />
              Confirmo que esta historia endodóntica fue valorada y registrada clínicamente.
            </label>
          </SectionCard>
        </div>

        <ModalFooter
          onClose={onClose}
          onPrimary={onSave}
          primaryLabel="Guardar historia de Endodoncia"
          disabled={saving || isReadOnly}
          loadingLabel="Guardando..."
        />
      </div>
    </div>
  )
}

export function PerioHistoryModal({
  open,
  onClose,
  form,
  setForm,
  saving,
  onSave,
  latestInstance,
  isReadOnly,
  readOnlyReason,
}) {
  if (!open) return null

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={modalCardStyle}>
        <ModalHeader
          badgeText="Historia especializada"
          badgeBg="#f0fdf4"
          badgeBorder="#bbf7d0"
          badgeColor="#166534"
          title="Historia de Periodoncia"
          description="Captura sangrado, sondaje, diagnóstico periodontal, factores de riesgo y plan de mantenimiento."
          latestInstance={latestInstance}
          onClose={onClose}
        />

        <ReadOnlyBanner
          isReadOnly={isReadOnly}
          readOnlyReason={readOnlyReason}
        />

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <SectionCard
            title="1. Consulta periodontal"
            subtitle="Motivo principal y fecha"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "220px minmax(0, 1fr)",
                gap: 12,
                alignItems: "end",
              }}
            >
              <Field label="Fecha">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Motivo de consulta periodontal">
                <input
                  value={form.chiefComplaint}
                  onChange={(e) => updateField("chiefComplaint", e.target.value)}
                  placeholder="Ej. sangrado gingival, movilidad, inflamación, mal aliento"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="2. Hallazgos clínicos"
            subtitle="Signos y síntomas relevantes"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Sangrado al cepillado">
                <select
                  value={form.bleedingOnBrushing}
                  onChange={(e) => updateField("bleedingOnBrushing", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>

              <Field label="Sangrado espontáneo">
                <select
                  value={form.spontaneousBleeding}
                  onChange={(e) => updateField("spontaneousBleeding", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>

              <Field label="Halitosis">
                <select
                  value={form.halitosis}
                  onChange={(e) => updateField("halitosis", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>

              <Field label="Sensibilidad">
                <select
                  value={form.sensitivity}
                  onChange={(e) => updateField("sensitivity", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="YES">Sí</option>
                  <option value="NO">No</option>
                </select>
              </Field>

              <Field label="Movilidad">
                <select
                  value={form.mobility}
                  onChange={(e) => updateField("mobility", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="NONE">Sin movilidad</option>
                  <option value="I">Grado I</option>
                  <option value="II">Grado II</option>
                  <option value="III">Grado III</option>
                </select>
              </Field>

              <Field label="Recesión gingival">
                <select
                  value={form.recession}
                  onChange={(e) => updateField("recession", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="NONE">No</option>
                  <option value="MILD">Leve</option>
                  <option value="MODERATE">Moderada</option>
                  <option value="SEVERE">Severa</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="3. Exploración periodontal"
            subtitle="Placa, cálculo y sondaje"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Nivel de placa">
                <select
                  value={form.plaqueLevel}
                  onChange={(e) => updateField("plaqueLevel", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="LOW">Bajo</option>
                  <option value="MEDIUM">Medio</option>
                  <option value="HIGH">Alto</option>
                </select>
              </Field>

              <Field label="Nivel de cálculo">
                <select
                  value={form.calculusLevel}
                  onChange={(e) => updateField("calculusLevel", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="LOW">Bajo</option>
                  <option value="MEDIUM">Medio</option>
                  <option value="HIGH">Alto</option>
                </select>
              </Field>

              <Field label="Índice de sangrado">
                <input
                  value={form.bleedingIndex}
                  onChange={(e) => updateField("bleedingIndex", e.target.value)}
                  placeholder="Ej. 20%, 35%, generalizado"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Resumen de sondaje periodontal">
                <textarea
                  value={form.probingSummary}
                  onChange={(e) => updateField("probingSummary", e.target.value)}
                  placeholder="Ej. 4-5 mm generalizado en sectores posteriores, 6 mm distal en 36..."
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="4. Diagnóstico y riesgo"
            subtitle="Clasificación clínica general"
            defaultOpen={false}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Diagnóstico periodontal">
                <select
                  value={form.periodontalDiagnosis}
                  onChange={(e) => updateField("periodontalDiagnosis", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="HEALTH">Salud periodontal</option>
                  <option value="GINGIVITIS">Gingivitis</option>
                  <option value="PERIODONTITIS_STAGE_I">Periodontitis estadio I</option>
                  <option value="PERIODONTITIS_STAGE_II">Periodontitis estadio II</option>
                  <option value="PERIODONTITIS_STAGE_III">Periodontitis estadio III</option>
                  <option value="PERIODONTITIS_STAGE_IV">Periodontitis estadio IV</option>
                </select>
              </Field>

              <Field label="Nivel de riesgo">
                <select
                  value={form.riskLevel}
                  onChange={(e) => updateField("riskLevel", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="LOW">Bajo</option>
                  <option value="MEDIUM">Medio</option>
                  <option value="HIGH">Alto</option>
                </select>
              </Field>

              <Field label="Tratamiento periodontal previo">
                <input
                  value={form.previousPeriodontalTreatment}
                  onChange={(e) => updateField("previousPeriodontalTreatment", e.target.value)}
                  placeholder="Ej. profilaxis, raspado, cirugía, mantenimiento"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Tabaquismo">
                <select
                  value={form.smokingStatus}
                  onChange={(e) => updateField("smokingStatus", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="NO">No</option>
                  <option value="YES">Sí</option>
                  <option value="FORMER">Exfumador</option>
                </select>
              </Field>

              <Field label="Diabetes">
                <select
                  value={form.diabetesStatus}
                  onChange={(e) => updateField("diabetesStatus", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="NO">No</option>
                  <option value="YES_CONTROLLED">Sí controlada</option>
                  <option value="YES_UNCONTROLLED">Sí no controlada</option>
                </select>
              </Field>

              <Field label="Intervalo de mantenimiento">
                <input
                  value={form.maintenanceInterval}
                  onChange={(e) => updateField("maintenanceInterval", e.target.value)}
                  placeholder="Ej. cada 3 meses"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="5. Plan periodontal"
            subtitle="Conducta clínica"
            defaultOpen={false}
          >
            <Field label="Plan de tratamiento periodontal">
              <textarea
                value={form.treatmentPlan}
                onChange={(e) => updateField("treatmentPlan", e.target.value)}
                placeholder="Ej. profilaxis, raspado y alisado radicular por cuadrantes, control de placa, reevaluación..."
                style={textareaStyle}
                disabled={isReadOnly}
              />
            </Field>

            <div style={{ marginTop: 12 }}>
              <Field label="Notas adicionales">
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="6. Validación"
            subtitle="Confirmación clínica"
            defaultOpen={true}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                color: "#334155",
                fontWeight: 600,
                cursor: isReadOnly ? "not-allowed" : "pointer",
                opacity: isReadOnly ? 0.75 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={form.consentAccepted}
                disabled={isReadOnly}
                onChange={(e) => updateField("consentAccepted", e.target.checked)}
              />
              Confirmo que esta historia periodontal fue valorada y registrada clínicamente.
            </label>
          </SectionCard>
        </div>

        <ModalFooter
          onClose={onClose}
          onPrimary={onSave}
          primaryLabel="Guardar historia de Periodoncia"
          disabled={saving || isReadOnly}
          loadingLabel="Guardando..."
        />
      </div>
    </div>
  )
}

export function SurgeryHistoryModal({
  open,
  onClose,
  form,
  setForm,
  saving,
  onSave,
  latestInstance,
  isReadOnly,
  readOnlyReason,
}) {
  if (!open) return null

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={modalCardStyle}>
        <ModalHeader
          badgeText="Historia especializada"
          badgeBg="#fef2f2"
          badgeBorder="#fecaca"
          badgeColor="#991b1b"
          title="Historia de Cirugía"
          description="Captura diagnóstico quirúrgico, procedimiento, técnica, hallazgos y cuidados postoperatorios."
          latestInstance={latestInstance}
          onClose={onClose}
        />

        <ReadOnlyBanner
          isReadOnly={isReadOnly}
          readOnlyReason={readOnlyReason}
        />

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <SectionCard
            title="1. Identificación quirúrgica"
            subtitle="Diagnóstico y acto quirúrgico"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Fecha">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Diagnóstico quirúrgico">
                <input
                  value={form.surgicalDiagnosis}
                  onChange={(e) => updateField("surgicalDiagnosis", e.target.value)}
                  placeholder="Ej. tercer molar retenido"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Procedimiento">
                <input
                  value={form.procedureType}
                  onChange={(e) => updateField("procedureType", e.target.value)}
                  placeholder="Ej. exodoncia / cirugía de terceros molares"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Pieza">
                <input
                  value={form.tooth}
                  onChange={(e) => updateField("tooth", e.target.value)}
                  placeholder="Ej. 38"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Área quirúrgica">
                  <input
                    value={form.surgicalArea}
                    onChange={(e) => updateField("surgicalArea", e.target.value)}
                    placeholder="Ej. sector posterior inferior izquierdo"
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="2. Técnica"
            subtitle="Datos del procedimiento"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Anestesia">
                <input
                  value={form.anesthesiaType}
                  onChange={(e) => updateField("anesthesiaType", e.target.value)}
                  placeholder="Ej. infiltrativa / troncular"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Detalle anestesia">
                <input
                  value={form.anesthesiaDetail}
                  onChange={(e) => updateField("anesthesiaDetail", e.target.value)}
                  placeholder="Ej. lidocaína 2% con epinefrina"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Tipo de colgajo">
                <input
                  value={form.flapType}
                  onChange={(e) => updateField("flapType", e.target.value)}
                  placeholder="Ej. triangular"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Incisión">
                <input
                  value={form.incisionType}
                  onChange={(e) => updateField("incisionType", e.target.value)}
                  placeholder="Ej. lineal"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Osteotomía">
                <input
                  value={form.osteotomy}
                  onChange={(e) => updateField("osteotomy", e.target.value)}
                  placeholder="Sí / No / detalle"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Odontosección">
                <input
                  value={form.odontosection}
                  onChange={(e) => updateField("odontosection", e.target.value)}
                  placeholder="Sí / No / detalle"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Complejidad de extracción">
                <input
                  value={form.extractionComplexity}
                  onChange={(e) => updateField("extractionComplexity", e.target.value)}
                  placeholder="Ej. simple / compleja"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Suturas">
                <input
                  value={form.sutures}
                  onChange={(e) => updateField("sutures", e.target.value)}
                  placeholder="Ej. seda 3-0"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="3. Hallazgos y evolución inmediata"
            subtitle="Lo encontrado durante el acto quirúrgico"
            defaultOpen={true}
          >
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Hallazgos">
                <textarea
                  value={form.findings}
                  onChange={(e) => updateField("findings", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Complicaciones">
                <textarea
                  value={form.complications}
                  onChange={(e) => updateField("complications", e.target.value)}
                  placeholder="Si no hubo, dejar en blanco o escribir Sin complicaciones"
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="4. Indicaciones y manejo postoperatorio"
            subtitle="Conducta al egreso"
            defaultOpen={false}
          >
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Indicaciones postoperatorias">
                <textarea
                  value={form.indications}
                  onChange={(e) => updateField("indications", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Medicamentos postoperatorios">
                <textarea
                  value={form.medicationsPostOp}
                  onChange={(e) => updateField("medicationsPostOp", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Plan de seguimiento">
                <textarea
                  value={form.followUpPlan}
                  onChange={(e) => updateField("followUpPlan", e.target.value)}
                  placeholder="Ej. control en 7 días / retiro de suturas"
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="5. Validación"
            subtitle="Confirmación clínica"
            defaultOpen={true}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                color: "#334155",
                fontWeight: 600,
                cursor: isReadOnly ? "not-allowed" : "pointer",
                opacity: isReadOnly ? 0.75 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={form.consentAccepted}
                disabled={isReadOnly}
                onChange={(e) => updateField("consentAccepted", e.target.checked)}
              />
              Confirmo que esta historia quirúrgica fue valorada y registrada clínicamente.
            </label>
          </SectionCard>
        </div>

        <ModalFooter
          onClose={onClose}
          onPrimary={onSave}
          primaryLabel="Guardar historia de Cirugía"
          disabled={saving || isReadOnly}
          loadingLabel="Guardando..."
        />
      </div>
    </div>
  )
}

export function OrthoHistoryModal({
  open,
  onClose,
  form,
  setForm,
  saving,
  onSave,
  latestInstance,
  isReadOnly,
  readOnlyReason,
}) {
  if (!open) return null

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={modalCardStyle}>
        <ModalHeader
          badgeText="Historia especializada"
          badgeBg="#faf5ff"
          badgeBorder="#e9d5ff"
          badgeColor="#7e22ce"
          title="Historia de Ortodoncia"
          description="Captura análisis ortodóntico inicial, relaciones esqueletales y dentales, objetivos y plan."
          latestInstance={latestInstance}
          onClose={onClose}
        />

        <ReadOnlyBanner
          isReadOnly={isReadOnly}
          readOnlyReason={readOnlyReason}
        />

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <SectionCard
            title="1. Consulta ortodóntica"
            subtitle="Motivo principal y análisis facial"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Fecha">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Perfil facial">
                <input
                  value={form.facialProfile}
                  onChange={(e) => updateField("facialProfile", e.target.value)}
                  placeholder="Ej. recto / convexo / cóncavo"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Análisis de sonrisa">
                <input
                  value={form.smileAnalysis}
                  onChange={(e) => updateField("smileAnalysis", e.target.value)}
                  placeholder="Ej. línea media desviada, sonrisa gingival"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Motivo de consulta">
                <textarea
                  value={form.chiefComplaint}
                  onChange={(e) => updateField("chiefComplaint", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="2. Relación esqueletal y oclusal"
            subtitle="Clasificación ortodóntica inicial"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Clase esqueletal">
                <input
                  value={form.skeletalClass}
                  onChange={(e) => updateField("skeletalClass", e.target.value)}
                  placeholder="Ej. Clase I / II / III"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Molar derecha">
                <input
                  value={form.molarClassRight}
                  onChange={(e) => updateField("molarClassRight", e.target.value)}
                  placeholder="Clase I"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Molar izquierda">
                <input
                  value={form.molarClassLeft}
                  onChange={(e) => updateField("molarClassLeft", e.target.value)}
                  placeholder="Clase I"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Canina derecha">
                <input
                  value={form.canineClassRight}
                  onChange={(e) => updateField("canineClassRight", e.target.value)}
                  placeholder="Clase I"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Canina izquierda">
                <input
                  value={form.canineClassLeft}
                  onChange={(e) => updateField("canineClassLeft", e.target.value)}
                  placeholder="Clase I"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="3. Mediciones y alteraciones"
            subtitle="Hallazgos ortodónticos principales"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Overjet">
                <input
                  value={form.overjet}
                  onChange={(e) => updateField("overjet", e.target.value)}
                  placeholder="Ej. 4 mm"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Overbite">
                <input
                  value={form.overbite}
                  onChange={(e) => updateField("overbite", e.target.value)}
                  placeholder="Ej. 30%"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Mordida cruzada">
                <input
                  value={form.crossbite}
                  onChange={(e) => updateField("crossbite", e.target.value)}
                  placeholder="Sí / No / detalle"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Mordida abierta">
                <input
                  value={form.openbite}
                  onChange={(e) => updateField("openbite", e.target.value)}
                  placeholder="Sí / No / detalle"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Apiñamiento superior">
                <input
                  value={form.crowdingUpper}
                  onChange={(e) => updateField("crowdingUpper", e.target.value)}
                  placeholder="Leve / moderado / severo"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Apiñamiento inferior">
                <input
                  value={form.crowdingLower}
                  onChange={(e) => updateField("crowdingLower", e.target.value)}
                  placeholder="Leve / moderado / severo"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Espaciamiento superior">
                <input
                  value={form.spacingUpper}
                  onChange={(e) => updateField("spacingUpper", e.target.value)}
                  placeholder="Leve / moderado / severo"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Espaciamiento inferior">
                <input
                  value={form.spacingLower}
                  onChange={(e) => updateField("spacingLower", e.target.value)}
                  placeholder="Leve / moderado / severo"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Línea media superior">
                <input
                  value={form.midlineUpper}
                  onChange={(e) => updateField("midlineUpper", e.target.value)}
                  placeholder="Centrada / desviada"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Línea media inferior">
                <input
                  value={form.midlineLower}
                  onChange={(e) => updateField("midlineLower", e.target.value)}
                  placeholder="Centrada / desviada"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="ATM">
                <input
                  value={form.tmjStatus}
                  onChange={(e) => updateField("tmjStatus", e.target.value)}
                  placeholder="Normal / ruidos / dolor"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Hábitos relacionados">
                <input
                  value={form.habits}
                  onChange={(e) => updateField("habits", e.target.value)}
                  placeholder="Ej. respiración bucal, succión digital"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="4. Antecedentes y plan"
            subtitle="Tratamiento previo, objetivos y plan inicial"
            defaultOpen={false}
          >
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Tratamiento ortodóntico previo">
                <textarea
                  value={form.previousOrthoTreatment}
                  onChange={(e) => updateField("previousOrthoTreatment", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Objetivos del tratamiento">
                <textarea
                  value={form.treatmentObjectives}
                  onChange={(e) => updateField("treatmentObjectives", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Plan ortodóntico inicial">
                <textarea
                  value={form.treatmentPlan}
                  onChange={(e) => updateField("treatmentPlan", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Notas adicionales">
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="5. Validación"
            subtitle="Confirmación clínica"
            defaultOpen={true}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                color: "#334155",
                fontWeight: 600,
                cursor: isReadOnly ? "not-allowed" : "pointer",
                opacity: isReadOnly ? 0.75 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={form.consentAccepted}
                disabled={isReadOnly}
                onChange={(e) => updateField("consentAccepted", e.target.checked)}
              />
              Confirmo que esta historia de Ortodoncia fue valorada y registrada clínicamente.
            </label>
          </SectionCard>
        </div>

        <ModalFooter
          onClose={onClose}
          onPrimary={onSave}
          primaryLabel="Guardar historia de Ortodoncia"
          disabled={saving || isReadOnly}
          loadingLabel="Guardando..."
        />
      </div>
    </div>
  )
}

export function PediatricHistoryModal({
  open,
  onClose,
  form,
  setForm,
  saving,
  onSave,
  latestInstance,
  isReadOnly,
  readOnlyReason,
}) {
  if (!open) return null

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={modalCardStyle}>
        <ModalHeader
          badgeText="Historia especializada"
          badgeBg="#ecfeff"
          badgeBorder="#a5f3fc"
          badgeColor="#0f766e"
          title="Historia de Odontopediatría"
          description="Captura antecedentes pediátricos, hábitos, experiencia dental, riesgo de caries y conducta clínica."
          latestInstance={latestInstance}
          onClose={onClose}
        />

        <ReadOnlyBanner
          isReadOnly={isReadOnly}
          readOnlyReason={readOnlyReason}
        />

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <SectionCard
            title="1. Identificación pediátrica"
            subtitle="Acompañante y motivo principal"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Fecha">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Acompañante">
                <input
                  value={form.companionName}
                  onChange={(e) => updateField("companionName", e.target.value)}
                  placeholder="Nombre del padre, madre o tutor"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Parentesco">
                <input
                  value={form.companionRelation}
                  onChange={(e) => updateField("companionRelation", e.target.value)}
                  placeholder="Ej. madre, padre, tutor"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Motivo de consulta">
                <textarea
                  value={form.chiefComplaint}
                  onChange={(e) => updateField("chiefComplaint", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="2. Antecedentes perinatales"
            subtitle="Datos de embarazo y nacimiento"
            defaultOpen={true}
          >
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Observaciones del embarazo">
                <textarea
                  value={form.pregnancyNotes}
                  onChange={(e) => updateField("pregnancyNotes", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                }}
              >
                <Field label="Tipo de nacimiento">
                  <input
                    value={form.birthType}
                    onChange={(e) => updateField("birthType", e.target.value)}
                    placeholder="Ej. parto natural / cesárea"
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </Field>

                <Field label="Complicaciones al nacer">
                  <input
                    value={form.birthComplications}
                    onChange={(e) => updateField("birthComplications", e.target.value)}
                    placeholder="Ej. ninguna / prematuro / incubadora"
                    style={inputStyle}
                    disabled={isReadOnly}
                  />
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="3. Alimentación y hábitos"
            subtitle="Factores funcionales y de riesgo"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Lactancia">
                <input
                  value={form.breastfeeding}
                  onChange={(e) => updateField("breastfeeding", e.target.value)}
                  placeholder="Ej. sí / no / duración"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Uso de biberón">
                <input
                  value={form.bottleUse}
                  onChange={(e) => updateField("bottleUse", e.target.value)}
                  placeholder="Ej. sí / no / nocturno"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Uso de chupón">
                <input
                  value={form.pacifierUse}
                  onChange={(e) => updateField("pacifierUse", e.target.value)}
                  placeholder="Ej. sí / no / duración"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Succión digital">
                <input
                  value={form.fingerSucking}
                  onChange={(e) => updateField("fingerSucking", e.target.value)}
                  placeholder="Ej. sí / no / frecuencia"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Respiración bucal">
                <input
                  value={form.mouthBreathing}
                  onChange={(e) => updateField("mouthBreathing", e.target.value)}
                  placeholder="Ej. sí / no / sospecha"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Consumo de azúcares">
                <input
                  value={form.sugarIntake}
                  onChange={(e) => updateField("sugarIntake", e.target.value)}
                  placeholder="Ej. alto / moderado / bajo"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="4. Desarrollo oral e higiene"
            subtitle="Erupción, cepillado y flúor"
            defaultOpen={false}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Erupción dentaria">
                <input
                  value={form.eruptionHistory}
                  onChange={(e) => updateField("eruptionHistory", e.target.value)}
                  placeholder="Ej. normal / retrasada"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Ayuda en cepillado">
                <input
                  value={form.brushingAssistance}
                  onChange={(e) => updateField("brushingAssistance", e.target.value)}
                  placeholder="Ej. con apoyo de mamá"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Exposición a flúor">
                <input
                  value={form.fluorideExposure}
                  onChange={(e) => updateField("fluorideExposure", e.target.value)}
                  placeholder="Ej. pasta fluorada / barniz"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Riesgo de caries">
                <select
                  value={form.cariesRisk}
                  onChange={(e) => updateField("cariesRisk", e.target.value)}
                  style={inputStyle}
                  disabled={isReadOnly}
                >
                  <option value="">Selecciona</option>
                  <option value="LOW">Bajo</option>
                  <option value="MEDIUM">Medio</option>
                  <option value="HIGH">Alto</option>
                </select>
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="5. Conducta y experiencia dental"
            subtitle="Cómo se comporta el paciente pediátrico"
            defaultOpen={false}
          >
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Experiencia dental previa">
                <textarea
                  value={form.previousDentalExperience}
                  onChange={(e) => updateField("previousDentalExperience", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Conducta durante la consulta">
                <textarea
                  value={form.behaviorDuringConsultation}
                  onChange={(e) => updateField("behaviorDuringConsultation", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="6. Plan pediátrico"
            subtitle="Conducta clínica propuesta"
            defaultOpen={false}
          >
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Plan de tratamiento">
                <textarea
                  value={form.treatmentPlan}
                  onChange={(e) => updateField("treatmentPlan", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>

              <Field label="Notas adicionales">
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  style={textareaStyle}
                  disabled={isReadOnly}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="7. Validación"
            subtitle="Confirmación clínica"
            defaultOpen={true}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                color: "#334155",
                fontWeight: 600,
                cursor: isReadOnly ? "not-allowed" : "pointer",
                opacity: isReadOnly ? 0.75 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={form.consentAccepted}
                disabled={isReadOnly}
                onChange={(e) => updateField("consentAccepted", e.target.checked)}
              />
              Confirmo que esta historia de Odontopediatría fue valorada y registrada clínicamente.
            </label>
          </SectionCard>
        </div>

        <ModalFooter
          onClose={onClose}
          onPrimary={onSave}
          primaryLabel="Guardar historia de Odontopediatría"
          disabled={saving || isReadOnly}
          loadingLabel="Guardando..."
        />
      </div>
    </div>
  )
}

export function QrPreIntakeReviewModal({
  open,
  onClose,
  qrIntake,
  onUseData,
  onMarkReviewed,
  markingReviewed,
}) {
  if (!open) return null

  const data = qrIntake?.dataJson || {}

  const complaintMap = {
    PAIN: "Dolor dental",
    CARIES: "Caries",
    GINGIVITIS: "Gingivitis",
    ORTHODONTICS: "Ortodoncia",
    ESTHETICS: "Estética",
    CLEANING: "Limpieza",
    FRACTURE: "Fractura dental",
    EXTRACTION: "Extracción dental",
    OTHER: "Otro",
  }

  const habitMap = {
    BRUXISM: "Bruxismo",
    MOUTH_BREATHING: "Respiración bucal",
    NAIL_BITING: "Onicofagia",
    BITE_OBJECTS: "Morder objetos",
    OTHER: "Otro",
  }

  const medicalMap = {
    DIABETES: "Diabetes",
    HYPERTENSION: "Hipertensión",
    CARDIOPATHY: "Cardiopatía",
    ALLERGIES: "Alergias",
    SYSTEMIC_DISEASE: "Enfermedad sistémica",
    PREGNANCY: "Embarazo",
    MEDICATION: "Toma medicamento",
  }

  const genderMap = {
    FEMALE: "Femenino",
    MALE: "Masculino",
    OTHER: "Otro",
  }

  const recommendedMap = {
    YES: "Sí",
    NO: "No",
  }

  const translateList = (values, map) => {
    if (!Array.isArray(values)) return ""
    return values.map((item) => map[item] || item || "").filter(Boolean).join(", ")
  }

  const translateValue = (value, map) => {
    return map[String(value || "").trim()] || value || ""
  }

  const complaintItems = Array.isArray(data.chiefComplaintTags)
    ? data.chiefComplaintTags
        .map((item) => complaintMap[item] || item)
        .filter(Boolean)
    : []

  if (
    Array.isArray(data.chiefComplaintTags) &&
    data.chiefComplaintTags.includes("OTHER") &&
    data.chiefComplaintOther
  ) {
    complaintItems.push(data.chiefComplaintOther)
  }

  const complaintTags = complaintItems.join(", ")
  const habits = translateList(data.habits, habitMap)
  const medicalConditions = translateList(data.medicalConditions, medicalMap)

  const receivedAt =
    qrIntake?.latestSubmittedAt ||
    qrIntake?.submittedAt ||
    qrIntake?.createdAt ||
    ""

  return (
    <div style={modalOverlayStyle}>
      <div style={{ ...modalCardStyle, width: "min(940px, 100%)" }}>
        <ModalHeader
          badgeText="Pre-registro del paciente"
          badgeBg="#eff6ff"
          badgeBorder="#bfdbfe"
          badgeColor="#1d4ed8"
          title="Captura QR recibida"
          description="Revisa lo que llenó el paciente antes de integrarlo al formulario clínico base o descartarlo como revisado."
          onClose={onClose}
        />

        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div
            style={{
              border: "1px solid #dbeafe",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
              borderRadius: 14,
              padding: 12,
              display: "grid",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <MetricPill label="Origen" value="QR del paciente" tone="info" />
              <MetricPill
                label="Recepción"
                value={
                  receivedAt
                    ? new Date(receivedAt).toLocaleString("es-MX")
                    : "Sin fecha"
                }
                tone="neutral"
              />
            </div>

            <div
              style={{
                fontSize: 13,
                color: "#334155",
                lineHeight: 1.55,
              }}
            >
              Usa esta vista solo para validar e importar. La corrección final del
              expediente sigue haciéndose después dentro del formulario base.
            </div>
          </div>

          <SectionCard
            title="Datos generales"
            subtitle="Información capturada por el paciente"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Nombre">
                <ReadOnlyValue
                  value={`${data.firstName || ""} ${data.lastName || ""}`.trim()}
                />
              </Field>

              <Field label="Teléfono">
                <ReadOnlyValue value={data.phone || ""} />
              </Field>

              <Field label="Fecha de nacimiento">
                <ReadOnlyValue
                  value={data.birthDate ? String(data.birthDate).slice(0, 10) : ""}
                />
              </Field>

              <Field label="Dirección">
                <ReadOnlyValue value={data.address || ""} />
              </Field>

              <Field label="Ocupación">
                <ReadOnlyValue value={data.occupation || ""} />
              </Field>

              <Field label="Escolaridad">
                <ReadOnlyValue value={data.education || ""} />
              </Field>

              <Field label="Estado civil">
                <ReadOnlyValue value={data.maritalStatus || ""} />
              </Field>

              <Field label="Género">
                <ReadOnlyValue value={translateValue(data.gender, genderMap)} />
              </Field>

              <Field label="¿Alguien lo recomendó?">
                <ReadOnlyValue
                  value={translateValue(data.recommended, recommendedMap)}
                />
              </Field>

              <Field label="Recomendado por">
                <ReadOnlyValue value={data.recommendedBy || ""} />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="Motivo de consulta"
            subtitle="Información clínica inicial"
            defaultOpen={true}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Motivos">
                <ReadOnlyValue value={complaintTags} />
              </Field>

              <Field label="Nivel de dolor">
                <ReadOnlyValue
                  value={
                    data.painLevel !== undefined &&
                    data.painLevel !== null &&
                    data.painLevel !== ""
                      ? String(data.painLevel)
                      : ""
                  }
                />
              </Field>

              <Field label="¿Desde cuándo?">
                <ReadOnlyValue value={data.symptomSince || ""} />
              </Field>
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Descripción">
                <ReadOnlyValue
                  value={data.chiefComplaintDescription || ""}
                  multiline={true}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="Antecedentes"
            subtitle="Hábitos, antecedentes dentales y condiciones médicas"
            defaultOpen={false}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Última consulta dental">
                <ReadOnlyValue
                  value={
                    data.lastDentalVisitDate
                      ? String(data.lastDentalVisitDate).slice(0, 10)
                      : ""
                  }
                />
              </Field>

              <Field label="Motivo de esa consulta">
                <ReadOnlyValue value={data.lastDentalVisitReason || ""} />
              </Field>

              <Field label="Hábitos">
                <ReadOnlyValue value={habits} />
              </Field>

              <Field label="Detalle del hábito">
                <ReadOnlyValue value={data.habitsDuration || ""} />
              </Field>

              <Field label="Condiciones médicas">
                <ReadOnlyValue value={medicalConditions} />
              </Field>

              <Field label="Medicamentos">
                <ReadOnlyValue value={data.medications || ""} />
              </Field>

              <Field label="Alergias">
                <ReadOnlyValue value={data.allergiesDetail || ""} />
              </Field>

              <Field label="Notas médicas">
                <ReadOnlyValue value={data.medicalNotes || ""} />
              </Field>

              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Observaciones">
                  <ReadOnlyValue value={data.observations || ""} multiline={true} />
                </Field>
              </div>
            </div>
          </SectionCard>
        </div>

        <ModalFooter
          onClose={onClose}
          onSecondary={onMarkReviewed}
          secondaryLabel="Marcar revisado sin usar"
          onPrimary={onUseData}
          primaryLabel="Usar datos y marcar revisado"
          disabled={markingReviewed}
          loadingLabel="Procesando..."
        />
      </div>
    </div>
  )
}